import { assert } from '@fe/core';
import { Reveal } from '@progress/kendo-react-animation';
import { Button, DropDownButton } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Checkbox, TextBox } from '@progress/kendo-react-inputs';
import clsx from 'clsx';
import { produce } from 'immer';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    AddOutline,
    AirportTowerOutline,
    ChevronDownOutline,
    ChevronUpOutline,
    EditOutline,
    SaveOutline,
    TrashOutline,
    UserGroupOutline
} from '../../../../../../../icons/icons';

import { useBlocker } from 'react-router';
import {
    AirportMonitorSettingsMerger,
    AirportThresholdLevelInheritanceInfo,
    AirportThresholdMergeResult,
    GroupHierarchyItem
} from '../../../../../../../monitoring/merger/airport-threshold-merger';
import { defaultAirportOptions, defaultAirportThresholds } from '../../../../../../../monitoring/monitor-defaults';
import {
    AirportThresholdGroupUsage,
    AirportThresholdType,
    CompanyThresholdsMode,
    MonitorAirportThreshold,
    MonitorAirportThresholdAlerts,
    MonitorAirportThresholdSettingsItem,
    MonitorAirportThresholdsGroupModel,
    ThresholdEnableState,
    ThresholdLevel
} from '../../../../../../../types/thresholds';
import { LoadingIndicatorCircular } from '../../../../../../components/indicators/LoadingIndicatorCircular';
import { useAppDispatch, useAppSelector } from '../../../../../../hooks/store-hooks';
import { updateMonitorSettings } from '../../../../../../state/slices/global-slice';
import { store } from '../../../../../../state/store';
import { SettingsNavTitle, useSettingsPageWidth } from '../../../../context/SettingsNavContext';
import { AirportAlertEditView } from '../common/AlertEditView';
import { AirportSettingsEditThresholdLevel } from '../common/SettingsEditThresholdLevel';
import { ThresholdHeaderBar } from '../common/ThresholdHeaderBar';
import { ThresholdsEditPopup } from '../common/ThresholdsEditPopup';
import { AirportThresholdAlertClickedArgs, AirportThresholdLevelClickedArgs } from '../common/ThresholdsGirdRow';
import { ThresholdsGrid } from '../common/ThresholdsGrid';

type MonitorAirportGroupDisplayModel = GroupHierarchyItem & { children: GroupHierarchyItem[] };
function useAirportThresholdsEditContext() {
    const companyFeaturesEnabled = useAppSelector((x) => x.user.rights.companyFeaturesEnabled);
    const displayOrder = useAppSelector((x) => x.global.monitorSettings.thresholds.airport.displayOrder);
    const thresholdDefinitions = useAppSelector((x) => x.global.monitorSettings.thresholds.airport.definitions);
    const merger = useMemo(() => new AirportMonitorSettingsMerger(), []);
    const [edited, setEdited] = useState(false);
    const [tab, setTab] = useState<'user' | 'company'>('user');
    const [companyMode, setCompanyMode] = useState(() => store.getState().global.monitorSettings.company.mode);

    const [groups, setGroups] = useState<GroupHierarchyItem[]>([]);
    const [selectedGroup, setSelectedGroup] = useState<MonitorAirportThresholdsGroupModel>();
    const [data, setData] = useState<Record<AirportThresholdType, AirportThresholdMergeResult>>();

    useEffect(() => {
        const state = store.getState();
        merger.setMode(companyFeaturesEnabled ? companyMode : CompanyThresholdsMode.notUsed);
        merger.setThresholdDefinitions(thresholdDefinitions);
        merger.setData({
            user: state.global.monitorSettings.user.airport,
            company: state.global.monitorSettings.company.airport
        });

        const groups = merger.getGroups(tab);
        const selected = groups[0];
        setGroups(groups);
        setSelectedGroup(selected);
        setData(merger.getThresholdsFor(tab, selected.airportId, selected.usage));
    }, [companyFeaturesEnabled, thresholdDefinitions]);

    const updateCompanyMode = useCallback(
        (mode: CompanyThresholdsMode) => {
            setCompanyMode(mode);
            merger.setMode(mode);
            const groups = merger.getGroups(tab);
            const selected = groups[0];
            setGroups(groups);
            setSelectedGroup(selected);
            setData(merger.getThresholdsFor(tab, selected.airportId, selected.usage));
            setEdited(true);
        },
        [merger, tab]
    );

    const selectGroup = useCallback(
        (group: MonitorAirportThresholdsGroupModel) => {
            setSelectedGroup(group);
            setData(merger.getThresholdsFor(tab, group.airportId, group.usage));
        },
        [merger, tab]
    );

    const changeTab = useCallback(
        (tab: 'user' | 'company') => {
            setTab(tab);
            const groups = merger.getGroups(tab);
            const selected = groups[0];
            setGroups(groups);
            setSelectedGroup(selected);
            setData(merger.getThresholdsFor(tab, selected.airportId, selected.usage));
        },
        [merger]
    );

    const getAlertContext = useCallback(
        (type: AirportThresholdType) => {
            return merger.getAlertsEditContext(tab, type);
        },
        [merger, tab]
    );

    const getThresholdLevelInfo = useCallback(
        (type: AirportThresholdType, level: ThresholdLevel) => {
            assert(selectedGroup);
            return merger.getThresholdLevelEditContext(tab, type, level, selectedGroup);
        },
        [merger, tab, selectedGroup]
    );

    const updateThresholdSettings = useCallback(
        (type: AirportThresholdType, recipe: (draft: MonitorAirportThresholdSettingsItem) => void) => {
            assert(selectedGroup);
            const data = merger.getData();

            const options =
                data[tab].thresholdsSettings.find((x) => x.type === type) ??
                ({
                    type,
                    priority: 50,
                    alerts: {
                        eventList: { value: null, minLevel: null },
                        eventListAck: { value: null, minLevel: null },
                        eventListImportant: { value: null, minLevel: null },
                        email: { value: null, minLevel: null },
                        logFile: { value: null, minLevel: null },
                        popup: { value: null, minLevel: null },
                        osAlert: { value: null, minLevel: null },
                        overrideColor: { value: null, minLevel: null },
                        voice: { value: null, minLevel: null },
                        playSound: { value: null, minLevel: null },
                        overrideColorHex: null
                    } satisfies MonitorAirportThresholdAlerts
                } satisfies MonitorAirportThresholdSettingsItem);

            const updated = produce(options, (x) => {
                // Don't remove this wrapper
                recipe(x);
            });

            merger.setData(
                produce(data, (draft) => {
                    const settings = draft[tab].thresholdsSettings;
                    const idx = settings.findIndex((x) => x.type === type);
                    if (idx === -1) {
                        settings.push(updated);
                    } else {
                        settings[idx] = updated;
                    }
                })
            );

            const newRow = merger.getThresholdFor(tab, type, selectedGroup);
            setData((p) => ({
                ...p!,
                [type]: newRow
            }));
            setEdited(true);
        },
        [tab, merger, selectedGroup]
    );

    const updateThreshold = useCallback(
        (type: AirportThresholdType, recipe: (draft: MonitorAirportThreshold) => void) => {
            assert(selectedGroup);
            const data = merger.getData();
            const group = data[tab].groups.find((x) => x.airportId === selectedGroup.airportId && x.usage === selectedGroup.usage);
            const threshold =
                group?.thresholds.find((x) => x.type === type) ??
                ({
                    type,
                    enableState: ThresholdEnableState.notSet,
                    info: null,
                    watch: null,
                    warning: null,
                    critical: null,
                    emergency: null,
                    ignoredInheritedLevels: []
                } satisfies MonitorAirportThreshold);

            const updated = produce(threshold, (x) => {
                // Don't remove this wrapper
                recipe(x);
            });

            merger.setData(
                produce(data, (draft) => {
                    const group = draft[tab].groups.find(
                        (x) => x.airportId === selectedGroup.airportId && x.usage === selectedGroup.usage
                    )!;
                    if (group) {
                        const index = group.thresholds.findIndex((x) => x.type === type);
                        if (index > -1) {
                            group.thresholds[index] = updated;
                        } else {
                            group.thresholds.push(updated);
                        }
                    }
                })
            );

            const newRow = merger.getThresholdFor(tab, type, selectedGroup);
            setData((p) => ({
                ...p!,
                [type]: newRow
            }));
            setEdited(true);
        },
        [tab, merger, selectedGroup]
    );

    const renameGroup = useCallback(
        (existing: MonitorAirportThresholdsGroupModel, newName: string) => {
            const data = merger.getData();
            if (data[tab].groups.some((x) => x.airportId === newName)) {
                return false;
            }

            merger.setData(
                produce(data, (draft) => {
                    const groups = draft[tab].groups;
                    for (const group of groups) {
                        if (group.airportId === existing.airportId) {
                            group.airportId = newName;
                        }
                    }
                })
            );
            const groups = merger.getGroups(tab);
            setGroups(groups);
            const newSelected = groups.find((x) => x.airportId === newName && x.usage === existing.usage);
            setSelectedGroup(newSelected);
            setData(merger.getThresholdsFor(tab, newSelected!.airportId, newSelected!.usage));
            setEdited(true);
            return true;
        },
        [tab, merger]
    );

    const addGroup = useCallback(
        (name: string, usage: AirportThresholdGroupUsage = AirportThresholdGroupUsage.default) => {
            const data = merger.getData();
            if (data[tab].groups.some((x) => x.airportId === name && x.usage === usage)) {
                return false;
            }

            merger.setData(
                produce(data, (draft) => {
                    draft[tab].groups.push({
                        airportId: name,
                        usage,
                        autoGenerated: false,
                        isOff: false,
                        thresholds: []
                    });
                })
            );
            const groups = merger.getGroups(tab);
            setGroups(groups);
            const newSelected = groups.find((x) => x.airportId === name);
            setSelectedGroup(newSelected);
            setData(merger.getThresholdsFor(tab, newSelected!.airportId, newSelected!.usage));
            setEdited(true);
            return true;
        },
        [tab, merger]
    );

    const removeGroup = useCallback(
        (deleteGroup: MonitorAirportThresholdsGroupModel) => {
            merger.setData(
                produce(merger.getData(), (draft) => {
                    if (deleteGroup.usage === AirportThresholdGroupUsage.default) {
                        // When deleting default delete every other usage
                        draft[tab].groups = draft[tab].groups.filter((x) => x.airportId !== deleteGroup.airportId);
                    } else {
                        draft[tab].groups = draft[tab].groups.filter(
                            (x) => x.airportId !== deleteGroup.airportId || x.usage !== deleteGroup.usage
                        );
                    }
                })
            );
            const groups = merger.getGroups(tab);
            setGroups(groups);

            if (selectedGroup) {
                if (selectedGroup.airportId === deleteGroup.airportId) {
                    if (deleteGroup.usage === AirportThresholdGroupUsage.default) {
                        // If default was remove use first group as selected
                        setSelectedGroup(groups[0]);
                        setData(merger.getThresholdsFor(tab, groups[0].airportId, groups[0].usage));
                    } else if (deleteGroup.usage === selectedGroup.usage) {
                        // If currently selected usage from that group was deleted
                        const newSelected = groups.find(
                            (x) => x.airportId === deleteGroup.airportId && x.usage === AirportThresholdGroupUsage.default
                        );
                        setSelectedGroup(newSelected);
                        setData(merger.getThresholdsFor(tab, newSelected!.airportId, newSelected!.usage));
                    }
                }
            }
            setEdited(true);
            return true;
        },
        [tab, merger, selectedGroup]
    );

    const setGroupOnOff = useCallback(
        (group: MonitorAirportThresholdsGroupModel, isOn: boolean) => {
            merger.setData(
                produce(merger.getData(), (draft) => {
                    const element = draft[tab].groups.find((x) => x.airportId === group.airportId && x.usage === group.usage);
                    if (element) {
                        element.isOff = !isOn;
                    }
                })
            );
            const groups = merger.getGroups(tab);
            setGroups(groups);
            const newSelected = groups.find((x) => x.airportId === selectedGroup?.airportId && x.usage === selectedGroup.usage);
            setSelectedGroup(newSelected);
            setData(merger.getThresholdsFor(tab, selectedGroup!.airportId, selectedGroup!.usage));
            setEdited(true);
        },
        [merger, selectedGroup, tab]
    );

    const setDefaults = useCallback(() => {
        assert(selectedGroup);
        const data = merger.getData();
        const newData = produce(data, (draft) => {
            const group = draft[tab].groups.find((x) => x.airportId === selectedGroup.airportId && x.usage === selectedGroup.usage);
            if (group) {
                group.thresholds = defaultAirportThresholds;
                draft[tab].thresholdsSettings = defaultAirportOptions;
            }
        });

        merger.setData(newData);
        const groups = merger.getGroups(tab);
        setGroups(groups);
        const newSelected = groups.find((x) => x.airportId === selectedGroup.airportId && x.usage === selectedGroup.usage);
        setSelectedGroup(newSelected);
        setData(merger.getThresholdsFor(tab, selectedGroup!.airportId, selectedGroup!.usage));
        setEdited(true);
    }, [tab, merger, selectedGroup]);

    const clearAll = useCallback(() => {
        assert(selectedGroup);
        const data = merger.getData();
        const newData = produce(data, (draft) => {
            const group = draft[tab].groups.find((x) => x.airportId === selectedGroup.airportId && x.usage === selectedGroup.usage);
            if (group) {
                group.thresholds = [];
                draft[tab].thresholdsSettings = [];
            }
        });

        merger.setData(newData);
        const groups = merger.getGroups(tab);
        setGroups(groups);
        const newSelected = groups.find((x) => x.airportId === selectedGroup.airportId && x.usage === selectedGroup.usage);
        setSelectedGroup(newSelected);
        setData(merger.getThresholdsFor(tab, selectedGroup!.airportId, selectedGroup!.usage));
        setEdited(true);
    }, [tab, merger, selectedGroup]);

    const clearEdited = useCallback(() => {
        setEdited(false);
    }, []);

    return {
        tab,
        groups,
        selectedGroup,
        companyMode,
        data,
        edited,
        thresholdDefinitions,
        displayOrder,
        clearEdited,
        updateCompanyMode,
        changeTab,
        selectGroup,
        getAlertContext,
        getThresholdLevelInfo,
        updateThresholdSettings,
        updateThreshold,
        addGroup,
        removeGroup,
        renameGroup,
        setGroupOnOff,
        setDefaults,
        clearAll,
        getData: merger.getData.bind(merger)
    };
}

type AirportAlertsEditInfo = AirportThresholdAlertClickedArgs & {
    definition: MonitorAirportThresholdAlerts | null;
    inherited: MonitorAirportThresholdAlerts | null;
    effective: MonitorAirportThresholdAlerts;
};
type AirportLevelEditInfo = AirportThresholdLevelClickedArgs & { info: AirportThresholdLevelInheritanceInfo };

export default function SettingsAirportThresholds() {
    useSettingsPageWidth('100%');
    const canEditCompanyThresholds = useAppSelector((x) => x.user.rights.canEditCompanyThresholds);
    const [editLevel, setEditLevel] = useState<AirportLevelEditInfo | null>(null);
    const [editAlert, setEditAlert] = useState<AirportAlertsEditInfo | null>(null);
    const [busy, setBusy] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const context = useAirportThresholdsEditContext();
    const {
        tab,
        data,
        companyMode,
        edited,
        thresholdDefinitions,
        displayOrder,
        clearEdited,
        changeTab,
        updateCompanyMode,
        getAlertContext,
        getThresholdLevelInfo,
        updateThresholdSettings,
        updateThreshold,
        setDefaults,
        clearAll,
        getData
    } = context;
    const blocker = useBlocker(edited);
    const dispatch = useAppDispatch();

    const handleThresholdOptionsEdited = useCallback(
        (type: AirportThresholdType, recipe: (draft: MonitorAirportThresholdSettingsItem) => void) => {
            updateThresholdSettings(type, recipe);
            setEditAlert((prev) => (prev ? { ...prev, ...getAlertContext(type) } : null));
        },
        [updateThresholdSettings, getAlertContext]
    );

    const handlePriorityChanged = useCallback(
        (type: AirportThresholdType, priority: number) => {
            updateThresholdSettings(type, (x) => (x.priority = priority));
        },
        [updateThresholdSettings]
    );

    const handleEnableStatusChanged = useCallback(
        (type: AirportThresholdType, enableState: ThresholdEnableState) => {
            updateThreshold(type, (x) => (x.enableState = enableState));
        },
        [updateThreshold]
    );

    const handleLevelEdited = useCallback(
        (type: AirportThresholdType, level: ThresholdLevel, recipe: (draft: MonitorAirportThreshold) => void) => {
            updateThreshold(type, recipe);
            setEditLevel((p) => ({
                ...p!,
                info: getThresholdLevelInfo(type, level)
            }));
        },
        [updateThreshold]
    );

    const handleLevelClicked = useCallback(
        (e: AirportThresholdLevelClickedArgs) => {
            setEditLevel({
                ...e,
                info: getThresholdLevelInfo(e.type, e.level)
            });
            setEditAlert(null);
        },
        [getThresholdLevelInfo]
    );

    const handleAlertsClicked = useCallback(
        (e: AirportThresholdAlertClickedArgs) => {
            setEditLevel(null);
            setEditAlert({ ...e, ...getAlertContext(e.type) });
        },
        [getAlertContext]
    );

    const handleSave = useCallback(async () => {
        const data = getData();
        setBusy(true);
        try {
            const result = await dispatch(
                updateMonitorSettings({
                    user: {
                        airport: {
                            thresholdsSettings: data.user.thresholdsSettings,
                            groups: data.user.groups
                        }
                    },
                    company: canEditCompanyThresholds
                        ? {
                              airport: {
                                  thresholdsSettings: data.company.thresholdsSettings,
                                  groups: data.company.groups
                              },
                              companyMode
                          }
                        : undefined
                })
            ).unwrap();

            if (result) {
                clearEdited();
                return true;
            }
        } catch {
            // Failed
        } finally {
            setBusy(false);
        }

        return false;
    }, [getData, companyMode, canEditCompanyThresholds]);

    const handleSaveConfirm = async () => {
        if (await handleSave()) {
            if (blocker.state === 'blocked') {
                blocker.proceed();
            }
        } else {
            if (blocker.state === 'blocked') {
                blocker.reset();
            }
        }
    };

    return (
        <div className="pb-2">
            <div className="k-card max-h-100" ref={containerRef}>
                {blocker.state === 'blocked' && (
                    <Dialog title="Airport thresholds" onClose={() => blocker.reset()}>
                        <div>Do you want to save pending changes?</div>
                        <DialogActionsBar>
                            <Button themeColor={'primary'} onClick={handleSaveConfirm} disabled={busy}>
                                Yes
                            </Button>
                            <Button fillMode={'outline'} onClick={() => blocker.proceed()} disabled={busy}>
                                No
                            </Button>
                        </DialogActionsBar>
                    </Dialog>
                )}

                <SettingsNavTitle>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>Airport thresholds & alerts</div>
                        {busy && <LoadingIndicatorCircular size="1.5rem" />}
                        <Button fillMode={'outline'} svgIcon={SaveOutline} disabled={!edited} onClick={handleSave}>
                            Save
                        </Button>
                    </div>
                </SettingsNavTitle>

                <ThresholdHeaderBar
                    tab={tab}
                    companyMode={companyMode}
                    onChangeCompanyMode={updateCompanyMode}
                    onChangeTab={changeTab}
                    onRestoreDefaultClicked={setDefaults}
                    onClearClicked={clearAll}
                />

                <AirportGroups context={context} />

                {data && (
                    <>
                        {editLevel && (
                            <ThresholdsEditPopup
                                reference={editLevel.element}
                                boundary={containerRef.current ?? undefined}
                                onClose={() => setEditLevel(null)}>
                                <AirportSettingsEditThresholdLevel
                                    kind="airport"
                                    mode={companyMode}
                                    type={editLevel.type}
                                    level={editLevel.level}
                                    thresholdHierarchy={editLevel.info}
                                    threshold={data[editLevel.type]}
                                    info={thresholdDefinitions[editLevel.type]}
                                    onEdit={handleLevelEdited}
                                />
                            </ThresholdsEditPopup>
                        )}
                        {editAlert && (
                            <ThresholdsEditPopup
                                placement="left"
                                reference={editAlert.element}
                                boundary={containerRef.current ?? undefined}
                                onClose={() => setEditAlert(null)}>
                                <AirportAlertEditView
                                    tab={tab}
                                    type={editAlert.type}
                                    alerts={editAlert}
                                    mode={companyMode}
                                    info={thresholdDefinitions[editAlert.type]}
                                    onEdit={handleThresholdOptionsEdited}
                                />
                            </ThresholdsEditPopup>
                        )}

                        <div className="p-2">
                            Airport: <span className="fw-bold">{context.selectedGroup?.airportId || 'Default'}</span>
                            <span>, </span>
                            usage: <span className="fw-bold">{usageNames[context.selectedGroup!.usage]} </span>
                        </div>
                        <div className="h-100 min-h-0 overflow-auto">
                            <ThresholdsGrid
                                type="airport"
                                readonly={tab === 'company' && !canEditCompanyThresholds}
                                displayOrder={displayOrder}
                                companyMode={companyMode}
                                data={data}
                                thresholdDefinitions={thresholdDefinitions}
                                onEnableChange={handleEnableStatusChanged}
                                onPriorityChange={handlePriorityChanged}
                                onLevelClicked={handleLevelClicked}
                                onAlertsClicked={handleAlertsClicked}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

type GroupUsageDropdownOption = { text: string; value: AirportThresholdGroupUsage };
function AirportGroups(props: { context: ReturnType<typeof useAirportThresholdsEditContext> }) {
    const { context } = props;
    const [collapsed, setCollapsed] = useState(false);
    const [addErrMsg, setAddErrMsg] = useState('');
    const [addActive, setAddActive] = useState<false | { editItem: MonitorAirportThresholdsGroupModel | null }>(false);
    const [editAirportId, setEditAirportId] = useState('');
    const [addUsageOptions, setAddUsageOptions] = useState<GroupUsageDropdownOption[]>([]);

    const handleSaveGroup = () => {
        assert(addActive);
        if (addActive.editItem) {
            if (!context.renameGroup(addActive.editItem, editAirportId)) {
                setAddErrMsg('Group already exists');
                return;
            }
        } else {
            if (!context.addGroup(editAirportId)) {
                setAddErrMsg('Group already exists');
                return;
            }
        }
        setAddActive(false);
    };

    const displayGroups = useMemo(() => toDisplayGroups(context.groups), [context.groups]);

    return (
        <div className="p-2">
            <div className="d-flex align-items-center gap-1">
                <Button
                    fillMode={'flat'}
                    className="button-small"
                    svgIcon={collapsed ? ChevronDownOutline : ChevronUpOutline}
                    onClick={() => setCollapsed(!collapsed)}
                />
                <div>Airports</div>
            </div>
            <Reveal className="z-1 w-100 overflow-x-auto">
                {!collapsed && (
                    <div className="ps-2 pt-2">
                        <div className="d-flex gap-2">
                            {displayGroups.map((airport) => (
                                <div
                                    key={airport.airportId}
                                    className={clsx(
                                        'border rounded-1 p-2 d-flex flex-column',
                                        airport.airportId === context.selectedGroup?.airportId && 'border-primary'
                                    )}
                                    style={{ minWidth: 145 }}>
                                    {/* title */}
                                    <div className="d-flex align-items-center justify-content-between">
                                        <span>
                                            {airport.airportId && <SvgIcon icon={AirportTowerOutline} className="icon-b-2" />}
                                            <span> {airport.airportId || 'Default'}</span>
                                        </span>

                                        {airport.airportId && (
                                            <div className="d-flex justify-content-end align-items-center">
                                                {airport.autoGenerated ? (
                                                    <SvgIcon icon={UserGroupOutline} />
                                                ) : (
                                                    <>
                                                        <Button
                                                            title="Rename group"
                                                            className="button-small"
                                                            fillMode={'flat'}
                                                            svgIcon={EditOutline}
                                                            onClick={() => {
                                                                setAddActive({ editItem: airport });
                                                                setAddErrMsg('');
                                                                setEditAirportId(airport.airportId);
                                                            }}
                                                        />
                                                        <Button
                                                            title="Remove group"
                                                            className="button-small"
                                                            fillMode={'flat'}
                                                            svgIcon={TrashOutline}
                                                            onClick={() => context.removeGroup(airport)}
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* usage */}
                                    <div className="secondary-text">Usage:</div>
                                    {airport.children.map((usage) => (
                                        <div
                                            className={clsx(
                                                usage === context.selectedGroup && 'fw-bold',
                                                'ps-0 cursor-pointer d-flex align-items-center'
                                            )}
                                            key={usage.usage}
                                            onClick={() => context.selectGroup(usage)}>
                                            <div className="flex-grow-1 d-flex gap-1 align-items-center">
                                                <Checkbox
                                                    checked={!usage.isOff}
                                                    onChange={(e) => context.setGroupOnOff(usage, e.value)}
                                                    disabled={props.context.companyMode === CompanyThresholdsMode.minimum && usage.forceOn}
                                                />
                                                <div className="flex-grow-1">{usageNames[usage.usage]}</div>
                                                {usage.usage !== AirportThresholdGroupUsage.default && !usage.autoGenerated && (
                                                    <Button
                                                        className="button-small"
                                                        fillMode={'flat'}
                                                        svgIcon={TrashOutline}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            context.removeGroup(usage);
                                                        }}
                                                    />
                                                )}
                                                {usage.autoGenerated && <SvgIcon icon={UserGroupOutline} />}
                                            </div>
                                        </div>
                                    ))}

                                    {airport.children.length < 4 && (
                                        <DropDownButton
                                            svgIcon={AddOutline}
                                            fillMode={'outline'}
                                            onFocus={() => setAddUsageOptions(getUsageDropdownOptions(airport))}
                                            onItemClick={(e) => context.addGroup(airport.airportId, e.item.value)}
                                            className="button-small align-self-end"
                                            items={addUsageOptions}
                                        />
                                    )}
                                </div>
                            ))}
                            <div
                                onClick={() => {
                                    setAddActive({ editItem: null });
                                    setAddErrMsg('');
                                    setEditAirportId('');
                                }}
                                className={'border rounded-1 p-2 d-flex align-items-center justify-content-center clickable flex-shrink-0'}
                                style={{ width: 125 }}>
                                <SvgIcon icon={AddOutline} />
                            </div>

                            {addActive && (
                                <Dialog title="Add airport" onClose={() => setAddActive(false)}>
                                    <div>Airport ID:</div>
                                    <TextBox
                                        placeholder="IATA/ICAO"
                                        value={editAirportId}
                                        minLength={3}
                                        maxLength={4}
                                        onKeyDown={(e) => e.key === 'Enter' && handleSaveGroup()}
                                        onChange={(e) => setEditAirportId((e.value as string).toUpperCase())}
                                    />
                                    <div className="text-danger pt-2">{addErrMsg}</div>
                                    <DialogActionsBar>
                                        <Button
                                            themeColor={'primary'}
                                            disabled={!editAirportId || editAirportId.length < 3 || editAirportId.length > 4}
                                            onClick={handleSaveGroup}>
                                            Add
                                        </Button>
                                        <Button onClick={() => setAddActive(false)} fillMode={'outline'}>
                                            Cancel
                                        </Button>
                                    </DialogActionsBar>
                                </Dialog>
                            )}
                        </div>
                    </div>
                )}
            </Reveal>
        </div>
    );
}

function toDisplayGroups(groups: MonitorAirportThresholdsGroupModel[]): MonitorAirportGroupDisplayModel[] {
    const result: MonitorAirportGroupDisplayModel[] = [];
    for (const items of Object.values(Object.groupBy(groups, (x) => x.airportId))) {
        const defaultUsage = items?.find((x) => x.usage === AirportThresholdGroupUsage.default);
        if (!defaultUsage) {
            // Shouldn't be possible
            continue;
        }
        result.push({
            ...defaultUsage,
            children: items?.toSorted((a, b) => a.usage - b.usage) ?? []
        });
    }

    return [
        // Put default first
        ...result.filter((x) => !x.airportId),
        ...result.filter((x) => x.airportId).sort((a, b) => a.airportId.localeCompare(b.airportId))
    ];
}

function getUsageDropdownOptions(airport: MonitorAirportGroupDisplayModel): GroupUsageDropdownOption[] {
    const options: GroupUsageDropdownOption[] = [];
    if (!airport.children.some((x) => x.usage === AirportThresholdGroupUsage.origin)) {
        options.push({ text: 'Origin', value: AirportThresholdGroupUsage.origin });
    }

    if (!airport.children.some((x) => x.usage === AirportThresholdGroupUsage.destination)) {
        options.push({ text: 'Destination', value: AirportThresholdGroupUsage.destination });
    }

    if (!airport.children.some((x) => x.usage === AirportThresholdGroupUsage.alternate)) {
        options.push({ text: 'Alternate', value: AirportThresholdGroupUsage.alternate });
    }

    return options;
}

const usageNames: Record<AirportThresholdGroupUsage, string> = {
    [AirportThresholdGroupUsage.default]: 'Default',
    [AirportThresholdGroupUsage.origin]: 'Origin',
    [AirportThresholdGroupUsage.destination]: 'Destination',
    [AirportThresholdGroupUsage.alternate]: 'Alternate'
};
